import type { AxiosResponse } from 'axios'
import { apiClient } from '../client'
import { API_ENDPOINTS } from '@/config/api'
import { handleApiError } from '@/utils/apiErrorHandling'
import type {
  SoftphoneActiveCallsResponse,
  SoftphoneActiveCallsResult,
  SoftphoneCallResponse,
  SoftphoneCallSession,
  SoftphoneCredentials,
  SoftphoneCredentialsResponse,
  SoftphoneHealthResponse,
  SoftphoneHistoryParams,
  SoftphoneHistoryResponse,
  SoftphoneQueueStatus,
  SoftphoneQueueStatusResponse,
  SoftphoneStartCallRequest,
  SoftphoneStartCallResponse,
  SoftphoneStatistics,
  SoftphoneStatisticsResponse,
} from '@/types/api/softphone'

const extractCalls = (value: unknown): SoftphoneCallSession[] => {
  if (!value || typeof value !== 'object') return []
  const data = value as {
    calls?: SoftphoneCallSession[]
    items?: SoftphoneCallSession[]
    data?: {
      calls?: SoftphoneCallSession[]
      items?: SoftphoneCallSession[]
    }
  }

  return data.calls || data.items || data.data?.calls || data.data?.items || []
}

const extractCall = (value: unknown): SoftphoneCallSession | null => {
  if (!value || typeof value !== 'object') return null
  const data = value as {
    call?: SoftphoneCallSession
    data?: {
      call?: SoftphoneCallSession
    }
  }

  return data.call || data.data?.call || null
}

const extractCredentials = (
  value: SoftphoneCredentialsResponse
): SoftphoneCredentials => {
  const candidate = value.data?.credentials || value.data

  if (
    !candidate?.username ||
    !candidate?.password ||
    !candidate?.sip_domain ||
    !candidate?.wss_uri
  ) {
    throw new Error('Softphone credentials response is incomplete')
  }

  return {
    username: candidate.username,
    password: candidate.password,
    sip_domain: candidate.sip_domain,
    wss_uri: candidate.wss_uri,
    ice_servers: candidate.ice_servers,
    iceServers: candidate.iceServers,
    expires_at: candidate.expires_at,
  }
}

const extractQueueStatus = (
  value: SoftphoneQueueStatusResponse
): SoftphoneQueueStatus => {
  const data = value.data || {}
  const queue = data.queue || (data as Partial<SoftphoneQueueStatus>)
  const callers = queue.callers || data.callers || []
  const callerCount =
    queue.callerCount ?? data.callerCount ?? data.caller_count ?? callers.length
  const dispatcherCount =
    queue.dispatcherCount ?? data.dispatcherCount ?? data.dispatcher_count ?? 0
  const dispatcherPosition =
    queue.dispatcherPosition ??
    data.dispatcherPosition ??
    data.dispatcher_position

  return {
    name: queue.name || data.name,
    callers,
    callerCount,
    dispatcherCount,
    dispatcherPosition,
    agents: queue.agents || data.agents,
    registeredDispatchers:
      queue.registeredDispatchers || data.registeredDispatchers,
    checkedAt: queue.checkedAt || data.checkedAt,
    degraded: queue.degraded || data.degraded,
    error: queue.error || data.error,
  }
}

const normalizeStartCallData = (
  data: SoftphoneStartCallResponse['data']
): SoftphoneStartCallResponse['data'] => {
  if (data.dial || !data.call) return data

  const target = data.call.dial_target || data.call.sip_uri
  if (!target) return data

  return {
    ...data,
    dial: {
      to_type: data.call.to_type || null,
      target,
      sip_uri: data.call.sip_uri,
    },
  }
}

class SoftphoneService {
  async getCredentials(): Promise<SoftphoneCredentials> {
    try {
      const response: AxiosResponse<SoftphoneCredentialsResponse> =
        await apiClient.get(API_ENDPOINTS.SOFTPHONE_CREDENTIALS)

      return extractCredentials(response.data)
    } catch (error) {
      throw handleApiError(error, 'getSoftphoneCredentials')
    }
  }

  async startUserCall(
    payload: SoftphoneStartCallRequest,
    idempotencyKey: string
  ): Promise<SoftphoneStartCallResponse['data']> {
    try {
      const response: AxiosResponse<SoftphoneStartCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALLS_START, payload, {
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
        })

      return normalizeStartCallData(response.data.data)
    } catch (error) {
      throw handleApiError(error, 'startSoftphoneCall')
    }
  }

  async acceptCall(callId: string): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALL_ACCEPT(callId))

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'acceptSoftphoneCall')
    }
  }

  async rejectCall(
    callId: string,
    reason?: string
  ): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALL_REJECT(callId), {
          ...(reason && { reason }),
        })

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'rejectSoftphoneCall')
    }
  }

  async hangUpCall(callId: string): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALL_HANGUP(callId))

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'hangUpSoftphoneCall')
    }
  }

  async holdCall(callId: string): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALL_HOLD(callId))

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'holdSoftphoneCall')
    }
  }

  async unholdCall(callId: string): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALL_UNHOLD(callId))

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'unholdSoftphoneCall')
    }
  }

  async transferCall(
    callId: string,
    destination: string,
    transferType: 'blind' | 'attended' = 'blind'
  ): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.post(API_ENDPOINTS.CALL_TRANSFER(callId), {
          destination,
          transferType,
        })

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'transferSoftphoneCall')
    }
  }

  async getCall(callId: string): Promise<SoftphoneCallSession | null> {
    try {
      const response: AxiosResponse<SoftphoneCallResponse> =
        await apiClient.get(API_ENDPOINTS.CALL_BY_ID(callId))

      return extractCall(response.data)
    } catch (error) {
      throw handleApiError(error, 'getSoftphoneCall')
    }
  }

  async listActiveCalls(limit = 50): Promise<SoftphoneActiveCallsResult> {
    try {
      const response: AxiosResponse<SoftphoneActiveCallsResponse> =
        await apiClient.get(API_ENDPOINTS.CALLS_ACTIVE, {
          params: { limit },
        })

      return {
        calls: extractCalls(response.data),
        queue: response.data.data?.queue
          ? extractQueueStatus({
              status: response.data.status,
              data: response.data.data.queue,
            })
          : undefined,
        count: response.data.data?.count,
      }
    } catch (error) {
      throw handleApiError(error, 'listActiveSoftphoneCalls')
    }
  }

  async getQueueStatus(dispatcherId?: string): Promise<SoftphoneQueueStatus> {
    try {
      const response: AxiosResponse<SoftphoneQueueStatusResponse> =
        await apiClient.get(API_ENDPOINTS.CALLS_QUEUE_STATUS, {
          params: {
            dispatcher_id: dispatcherId,
          },
        })

      return extractQueueStatus(response.data)
    } catch (error) {
      throw handleApiError(error, 'getSoftphoneQueueStatus')
    }
  }

  async getCallHistory(params: SoftphoneHistoryParams = {}): Promise<{
    calls: SoftphoneCallSession[]
    nextToken?: string
    lastKey?: string
  }> {
    try {
      const response: AxiosResponse<SoftphoneHistoryResponse> =
        await apiClient.get(API_ENDPOINTS.CALLS_HISTORY, {
          params: {
            limit: params.limit || 50,
            actor_id: params.actor_id,
            case_id: params.case_id,
            phone_number: params.phone_number,
            lastKey: params.lastKey,
          },
        })

      return {
        calls: extractCalls(response.data),
        nextToken: response.data.data?.nextToken,
        lastKey: response.data.data?.lastKey || response.data.data?.nextToken,
      }
    } catch (error) {
      throw handleApiError(error, 'getSoftphoneCallHistory')
    }
  }

  async getStatistics(): Promise<SoftphoneStatistics> {
    try {
      const response: AxiosResponse<SoftphoneStatisticsResponse> =
        await apiClient.get(API_ENDPOINTS.CALLS_STATISTICS)

      return (
        response.data.data?.statistics ||
        response.data.data?.counts ||
        (response.data.data as SoftphoneStatistics) ||
        {}
      )
    } catch (error) {
      throw handleApiError(error, 'getSoftphoneStatistics')
    }
  }

  async getHealth(): Promise<SoftphoneHealthResponse['data']> {
    try {
      const response: AxiosResponse<SoftphoneHealthResponse> =
        await apiClient.get(API_ENDPOINTS.CALLS_HEALTH, {
          headers: { 'skip-auth': true },
        })

      return response.data.data
    } catch (error) {
      throw handleApiError(error, 'getSoftphoneHealth')
    }
  }
}

export const softphoneService = new SoftphoneService()
export default softphoneService
